import { useId, useState } from 'react';

import {
  OFFERED_SCOPES,
  SCOPE_LABELS,
  useApiTokens,
  useCreateApiToken,
  useRevokeApiToken,
  type ApiToken,
  type ApiTokenScope,
} from '@/lib/api-tokens';
import { saveMessage } from '@/lib/apps';
import { day } from '@/lib/dates';
import { McpConnectGuide } from './McpConnectGuide';

/**
 * Personal API tokens — how Claude Code, or a script, signs in to the MCP
 * endpoint and works on the studio's apps and posts.
 *
 * ⚠️ **The token is shown once.** The server stores its hash, like a session
 * token, and cannot show it again; the list shows the first characters so
 * one can be told from another. Making one asks for the password again — a
 * session is a cookie somebody may have left open, a token is a credential
 * that lives in a config file for three months.
 *
 * Scopes are exact: "Write posts" does not include reading them, and a
 * token handed to an assistant that writes gets no more than it writes with.
 */
export function ApiTokensCard() {
  const headingId = useId();
  const tokens = useApiTokens();
  const create = useCreateApiToken();
  const revoke = useRevokeApiToken();

  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiTokenScope[]>([
    'apps:read',
    'apps:write',
    'posts:read',
    'posts:write',
    'files:write',
  ]);
  const [password, setPassword] = useState('');
  /** The one showing of the token just made. Gone on the next render that makes another, or on dismiss. */
  const [issued, setIssued] = useState<{ token: string; record: ApiToken } | null>(null);
  const [copied, setCopied] = useState(false);

  const toggle = (scope: ApiTokenScope) =>
    setScopes((current) =>
      current.includes(scope) ? current.filter((one) => one !== scope) : [...current, scope],
    );

  const canMake = name.trim() !== '' && scopes.length > 0 && password !== '' && !create.isPending;
  const make = () => {
    if (!canMake) return;
    create.mutate(
      { name: name.trim(), scopes, password },
      {
        onSuccess: (made) => {
          setIssued(made);
          setCopied(false);
          setName('');
          setPassword('');
        },
        // Whatever happened, the password does not stay in the box.
        onError: () => setPassword(''),
      },
    );
  };

  const copy = async () => {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.token);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const endpoint = tokens.data?.mcpEndpoint ?? '…/mcp';
  const live = (tokens.data?.tokens ?? []).filter((one) => one.revokedAt === null);
  const gone = (tokens.data?.tokens ?? []).filter((one) => one.revokedAt !== null);

  return (
    <section aria-labelledby={headingId} className="panel flex flex-col gap-2.5 px-4 py-3.5">
      <span className="flex flex-wrap items-center gap-2.5">
        <h2 id={headingId} className="eyebrow m-0">
          MCP server
        </h2>
        <span className="ml-auto font-mono text-[10px]" style={{ color: 'var(--dim)' }}>
          {endpoint}
        </span>
      </span>
      <p className="m-0 text-[12px] leading-[1.5]" style={{ color: 'var(--muted)' }}>
        Lets Claude Code, Claude Desktop, ChatGPT or a script work on apps and posts through the
        endpoint on the right. Claude Code and scripts sign in with a personal token made here;
        ChatGPT and Claude&apos;s connectors sign in with OAuth and appear here once allowed. Either
        lasts 90 days and can be revoked here at any time. Changing your password revokes them all.
      </p>

      {issued ? (
        <div
          role="status"
          className="flex flex-col gap-2 rounded-lg px-[11px] py-[9px]"
          style={{
            background: 'color-mix(in oklab, var(--ok) 10%, transparent)',
            border: '1px solid color-mix(in oklab, var(--ok) 35%, transparent)',
          }}
        >
          <span className="text-[12px] font-medium">
            “{issued.record.name}” is ready. Copy it now — it will not be shown again. The setup
            under Connect below has it filled in.
          </span>
          <span className="flex flex-wrap items-center gap-2">
            <code className="field field-mono min-w-0 flex-1 overflow-x-auto py-1.5 text-[11.5px]">
              {issued.token}
            </code>
            <button type="button" onClick={copy} className="btn-small focus-ring cursor-pointer">
              {copied ? 'Copied' : 'Copy'}
            </button>
          </span>
          <button
            type="button"
            onClick={() => setIssued(null)}
            className="btn-small focus-ring cursor-pointer self-start"
          >
            Done
          </button>
        </div>
      ) : null}

      <div data-rows>
        {live.map((token) => (
          <TokenRow key={token.id} token={token} onRevoke={() => revoke.mutate(token.id)} />
        ))}
        {live.length === 0 && !tokens.isPending ? (
          <p className="m-0 py-2 text-[12.5px]" style={{ color: 'var(--muted)' }}>
            {gone.length > 0 ? 'No live tokens.' : 'No tokens yet.'}
          </p>
        ) : null}
        {gone.map((token) => (
          <TokenRow key={token.id} token={token} />
        ))}
      </div>
      {revoke.isError ? (
        <span className="text-[11.5px]" role="alert" style={{ color: 'var(--danger)' }}>
          {saveMessage(revoke.error)}
        </span>
      ) : null}

      <form
        className="flex flex-col gap-2.5 border-t pt-3"
        style={{ borderColor: 'var(--line)' }}
        onSubmit={(event) => {
          event.preventDefault();
          make();
        }}
      >
        <span className="field-label">New token</span>
        <div className="grid gap-2.5 [grid-template-columns:minmax(0,1fr)_minmax(0,1fr)]">
          <label className="flex min-w-0 flex-col gap-1.5">
            <span className="text-[11px]" style={{ color: 'var(--muted)' }}>
              Name · where it will live
            </span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Claude Code on the MacBook"
              maxLength={80}
              className="field w-full"
              autoComplete="off"
            />
          </label>
          <label className="flex min-w-0 flex-col gap-1.5">
            <span className="text-[11px]" style={{ color: 'var(--muted)' }}>
              Your password · asked again on purpose
            </span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="field w-full"
              autoComplete="current-password"
            />
          </label>
        </div>
        <fieldset className="m-0 flex flex-wrap gap-x-4 gap-y-1.5 border-0 p-0">
          <legend className="mb-1.5 text-[11px]" style={{ color: 'var(--muted)' }}>
            What it may do
          </legend>
          {OFFERED_SCOPES.map((scope) => (
            <label key={scope} className="flex items-center gap-1.5 text-[12px]">
              <input
                type="checkbox"
                checked={scopes.includes(scope)}
                onChange={() => toggle(scope)}
              />
              {SCOPE_LABELS[scope]}
              <code className="font-mono text-[10px]" style={{ color: 'var(--dim)' }}>
                {scope}
              </code>
            </label>
          ))}
        </fieldset>
        <span className="flex flex-wrap items-center gap-2.5">
          <button
            type="submit"
            disabled={!canMake}
            className="btn-accent focus-ring min-h-8 cursor-pointer px-[13px] py-[7px] text-[12.5px] disabled:cursor-default disabled:opacity-50"
          >
            {create.isPending ? 'Making…' : 'Make token'}
          </button>
          {create.isError ? (
            <span className="text-[11.5px]" role="alert" style={{ color: 'var(--danger)' }}>
              {saveMessage(create.error)}
            </span>
          ) : null}
        </span>
      </form>

      <McpConnectGuide endpoint={endpoint} token={issued?.token} />
    </section>
  );
}

function TokenRow({ token, onRevoke }: { token: ApiToken; onRevoke?: () => void }) {
  const revoked = token.revokedAt !== null;
  /*
   * An OAuth grant's row carries two dates: the hour its current access
   * token is good for (`expiresAt`, renewed in place) and when the grant
   * itself ends. The second is the one a person means by "expires".
   */
  const ends = token.refreshExpiresAt ?? token.expiresAt;
  const expired = !revoked && Date.parse(ends) <= Date.now();
  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b py-2"
      style={{ borderColor: 'var(--line)', opacity: revoked ? 0.55 : 1 }}
    >
      <span className="min-w-0 flex-1 text-[12.5px] font-medium">
        {token.name}
        {token.client ? (
          <span className="ml-2 font-normal" style={{ color: 'var(--muted)' }}>
            via OAuth
          </span>
        ) : null}
      </span>
      <code className="font-mono text-[10.5px]" style={{ color: 'var(--dim)' }}>
        {token.prefix}…
      </code>
      <span className="font-mono text-[10px]" style={{ color: 'var(--dim)' }}>
        {token.scopes.join(' ')}
      </span>
      <span
        className="font-mono text-[10px]"
        style={{ color: revoked || expired ? 'var(--danger)' : 'var(--dim)' }}
      >
        {revoked
          ? `revoked ${day(token.revokedAt!)}`
          : expired
            ? `expired ${day(ends)}`
            : `expires ${day(ends)}`}
        {' · '}
        {token.lastUsedAt ? `used ${day(token.lastUsedAt)}` : 'never used'}
      </span>
      {onRevoke && !revoked ? (
        <button type="button" onClick={onRevoke} className="btn-small focus-ring cursor-pointer">
          Revoke
        </button>
      ) : null}
    </div>
  );
}
